import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/adminAPI';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({
    name: '',
    logo: '',
    email: '',
    event_id: '',
    country: '',
    business_type: '',
    description_1: '',
    description_2: '',
    description_3: '',
    company_description: ''
  });
  const [events, setEvents] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [showEvents, setShowEvents] = useState(false);
  const [showBuyers, setShowBuyers] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showMeetings, setShowMeetings] = useState(false);
  const [convertingUser, setConvertingUser] = useState(null);
  const [newBuyerFromUser, setNewBuyerFromUser] = useState({
    name: '',
    surname: '',
    company_name: '',
    phone: '',
    notes: '',
    event_id: ''
  });
  const [newEvent, setNewEvent] = useState({
    name: '',
    event_date: '',
    start_time: '',
    end_time: '',
    slot_duration: 15
  });
  const [newBuyer, setNewBuyer] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    company_name: '',
    notes: '',
    event_id: ''
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [companyPasswords, setCompanyPasswords] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [buyerPasswords, setBuyerPasswords] = useState({});
  const [showBuyerPasswordModal, setShowBuyerPasswordModal] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    fetchEvents();
  }, []);

  const fetchData = async () => {
    try {
      const [statsResponse, companiesResponse, passwordsResponse] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getCompanies(),
        adminAPI.getCompanyPasswords()
      ]);

      setStats(statsResponse.data.stats);
      setCompanies(companiesResponse.data.companies);
      setCompanyPasswords(passwordsResponse.data.passwords || {});
    } catch (error) {
      setError('Error loading data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await adminAPI.getEvents();
      setEvents(response.data.events);
    } catch (error) {
      setError('Error loading events');
      console.error('Error fetching events:', error);
    }
  };

  const fetchBuyers = async () => {
    try {
      const response = await adminAPI.getBuyers();
      setBuyers(response.data.buyers);
    } catch (error) {
      setError('Error loading buyers');
      console.error('Error fetching buyers:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data.users);
    } catch (error) {
      setError('Error loading users');
      console.error('Error fetching users:', error);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await adminAPI.getAllMeetings();
      setMeetings(response.data.meetings);
    } catch (error) {
      setError('Error loading meetings');
      console.error('Error fetching meetings:', error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setLogoPreview(null);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      let logoUrl = newCompany.logo;
      if (logoFile) {
        try {
          const response = await adminAPI.uploadLogo(logoFile);
          logoUrl = response.data.url;
        } catch (error) {
          setError(error.response?.data?.message || "Error uploading logo.");
          return;
        }
      }
      // Convert description fields to JSON
      const descriptionJson = JSON.stringify({
        description_1: newCompany.description_1 || '',
        description_2: newCompany.description_2 || '',
        description_3: newCompany.description_3 || ''
      });
      const companyData = {
        ...newCompany,
        logo: logoUrl,
        description: descriptionJson
      };
      // Remove description_1, description_2, description_3 from data
      delete companyData.description_1;
      delete companyData.description_2;
      delete companyData.description_3;
      await adminAPI.createCompany(companyData);
      setShowAddCompany(false);
      setNewCompany({ name: '', logo: '', email: '', event_id: '', country: '', business_type: '', description_1: '', description_2: '', description_3: '', company_description: '' });
      fetchData();
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Error adding company');
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    // Parse description if it's JSON, otherwise use empty strings
    let desc1 = '', desc2 = '', desc3 = '';
    if (company.description) {
      try {
        const descObj = typeof company.description === 'string' ? JSON.parse(company.description) : company.description;
        desc1 = descObj.description_1 || descObj.field1 || '';
        desc2 = descObj.description_2 || descObj.field2 || '';
        desc3 = descObj.description_3 || descObj.field3 || '';
      } catch (e) {
        // If not JSON, treat as single description
        desc1 = company.description;
      }
    }
    setNewCompany({
      name: company.name,
      logo: company.logo || '',
      email: company.email,
      event_id: company.event_id || '',
      country: company.country || '',
      business_type: company.business_type || '',
      description_1: desc1,
      description_2: desc2,
      description_3: desc3,
      company_description: company.company_description || ''
    });
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      let logoUrl = newCompany.logo;
      if (logoFile) {
        try {
          const response = await adminAPI.uploadLogo(logoFile);
          logoUrl = response.data.url;
        } catch (error) {
          setError(error.response?.data?.message || "Error uploading logo.");
          return;
        }
      }
      // Convert description fields to JSON
      const descriptionJson = JSON.stringify({
        description_1: newCompany.description_1 || '',
        description_2: newCompany.description_2 || '',
        description_3: newCompany.description_3 || ''
      });
      const companyData = {
        ...newCompany,
        logo: logoUrl,
        description: descriptionJson
      };
      // Remove description_1, description_2, description_3 from data
      delete companyData.description_1;
      delete companyData.description_2;
      delete companyData.description_3;
      await adminAPI.updateCompany(editingCompany.id, companyData);
      setEditingCompany(null);
      setNewCompany({ name: '', logo: '', email: '', event_id: '', country: '', business_type: '', description_1: '', description_2: '', description_3: '', company_description: '' });
      fetchData();
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating company');
    }
  };

  const handleDeleteCompany = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await adminAPI.deleteCompany(id);
        fetchData();
      } catch (error) {
        setError(error.response?.data?.message || 'Error deleting company');
      }
    }
  };

  const cancelEdit = () => {
    setShowAddCompany(false);
    setEditingCompany(null);
    setNewCompany({ name: '', logo: '', email: '', event_id: '', country: '', business_type: '', description: '', company_description: '' });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleExportMeetings = async () => {
    try {
      const response = await adminAPI.exportMeetingsToExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meetings_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Error exporting meetings');
      console.error('Error exporting meetings:', error);
    }
  };

  const handleGeneratePassword = async (companyId) => {
    try {
      const response = await adminAPI.generateRandomPassword(companyId);
      setCompanyPasswords(prev => ({
        ...prev,
        [companyId]: response.data.password
      }));
    } catch (error) {
      setError(error.response?.data?.message || 'Error generating password');
    }
  };

  const handleResetPassword = async (companyId) => {
    const newPassword = prompt('Enter new password:');
    if (newPassword) {
      try {
        const response = await adminAPI.resetCompanyPassword(companyId, newPassword);
        setCompanyPasswords(prev => ({
          ...prev,
          [companyId]: response.data.password
        }));
      } catch (error) {
        setError(error.response?.data?.message || 'Error resetting password');
      }
    }
  };

  const handleGenerateBuyerPassword = async (buyerId) => {
    try {
      const response = await adminAPI.generateRandomBuyerPassword(buyerId);
      setBuyerPasswords(prev => ({
        ...prev,
        [buyerId]: response.data.password
      }));
    } catch (error) {
      setError(error.response?.data?.message || 'Error generating password');
    }
  };

  const handleResetBuyerPassword = async (buyerId) => {
    const newPassword = prompt('Enter new password:');
    if (newPassword) {
      try {
        const response = await adminAPI.resetBuyerPassword(buyerId, newPassword);
        setBuyerPasswords(prev => ({
          ...prev,
          [buyerId]: response.data.password
        }));
      } catch (error) {
        setError(error.response?.data?.message || 'Error resetting password');
      }
    }
  };

  const handleConvertUserToBuyer = async (e, userId) => {
    e.preventDefault();
    try {
      await adminAPI.convertUserToBuyer(userId, newBuyerFromUser);
      setConvertingUser(null);
      setNewBuyerFromUser({ name: '', surname: '', company_name: '', phone: '', notes: '', event_id: '' });
      fetchUsers();
      fetchBuyers(); // Refresh buyers list
      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Error converting user to buyer');
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    return typeof time === 'string' ? time.substring(0, 5) : time;
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString('tr-TR');
    } catch (e) {
      return date;
    }
  };

  const openPasswordModal = (companyId) => {
    setShowPasswordModal(companyId);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Super Admin Dashboard</h1>
      
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#721c24',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#007bff', margin: '0 0 10px 0' }}>Total Companies</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{stats.total_companies}</p>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#28a745', margin: '0 0 10px 0' }}>Total Meetings</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{stats.total_meetings}</p>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#ffc107', margin: '0 0 10px 0' }}>Pending Meetings</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{stats.pending_meetings}</p>
          </div>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#17a2b8', margin: '0 0 10px 0' }}>Approved Meetings</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{stats.approved_meetings}</p>
          </div>
        </div>
      )}

      {/* Company Management */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Company Management</h2>
          <button
            onClick={() => setShowAddCompany(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add New Company
          </button>
        </div>

        {/* Şirket Addme/Editme Formu */}
        {(showAddCompany || editingCompany) && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <h3>{editingCompany ? 'Edit Company' : 'Add New Company'}</h3>
            <form onSubmit={editingCompany ? handleUpdateCompany : handleAddCompany}>
              <div style={{ marginBottom: '15px' }}>
                <label>Company Name:</label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Company Email:</label>
                <input
                  type="email"
                  value={newCompany.email}
                  onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Company Logo:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
                {logoPreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Event (Fuar):</label>
                <select
                  value={newCompany.event_id}
                  onChange={(e) => setNewCompany({...newCompany, event_id: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                >
                  <option value="">Select Event</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>{event.name} - {event.event_date}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Country:</label>
                <input
                  type="text"
                  value={newCompany.country}
                  onChange={(e) => setNewCompany({...newCompany, country: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Business Type:</label>
                <input
                  type="text"
                  value={newCompany.business_type}
                  onChange={(e) => setNewCompany({...newCompany, business_type: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Description 1:</label>
                <input
                  type="text"
                  value={newCompany.description_1}
                  onChange={(e) => setNewCompany({...newCompany, description_1: e.target.value})}
                  placeholder="Enter description 1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Description 2:</label>
                <input
                  type="text"
                  value={newCompany.description_2}
                  onChange={(e) => setNewCompany({...newCompany, description_2: e.target.value})}
                  placeholder="Enter description 2"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Description 3:</label>
                <input
                  type="text"
                  value={newCompany.description_3}
                  onChange={(e) => setNewCompany({...newCompany, description_3: e.target.value})}
                  placeholder="Enter description 3"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label>Company Description (300-400 chars):</label>
                <textarea
                  value={newCompany.company_description}
                  onChange={(e) => setNewCompany({...newCompany, company_description: e.target.value})}
                  rows="4"
                  maxLength={400}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '5px'
                  }}
                />
                <small style={{ color: '#666' }}>{newCompany.company_description?.length || 0}/400 characters</small>
              </div>
              <div>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}
                >
                  {editingCompany ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Company List */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '15px'
        }}>
          {companies.map((company) => (
            <div
              key={company.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa'
              }}
            >
              <h4 style={{ margin: '0 0 10px 0' }}>{company.name}</h4>
              <p style={{ margin: '5px 0', color: '#666' }}>
                <strong>Email (Admin):</strong> {company.email || 'Not specified'}
              </p>
              <p style={{ margin: '5px 0', color: '#666' }}>
                <strong>Admin User:</strong> {company.admin ? company.admin.name : 'Not assigned'}
              </p>
              {company.country && (
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Country:</strong> {company.country}
                </p>
              )}
              {company.business_type && (
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Business Type:</strong> {company.business_type}
                </p>
              )}
              
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={() => handleEditCompany(company)}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => openPasswordModal(company.id)}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#EDB404',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Password
                </button>
                <button
                  onClick={() => handleDeleteCompany(company.id)}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Event Management */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Event Management (Fuar Yönetimi)</h2>
          <button
            onClick={() => {
              setShowEvents(!showEvents);
              if (!showEvents) fetchEvents();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showEvents ? 'Hide Events' : 'Show Events'}
          </button>
        </div>

        {showEvents && (
          <div>
            <button
              onClick={() => setEditingEvent({})}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '20px'
              }}
            >
              Add New Event
            </button>

            {editingEvent !== null && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <h3>{editingEvent.id ? 'Edit Event' : 'Add New Event'}</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingEvent.id) {
                      await adminAPI.updateEvent(editingEvent.id, newEvent);
                    } else {
                      await adminAPI.createEvent(newEvent);
                    }
                    setEditingEvent(null);
                    setNewEvent({ name: '', event_date: '', start_time: '', end_time: '', slot_duration: 15 });
                    fetchEvents();
                  } catch (error) {
                    setError(error.response?.data?.message || 'Error saving event');
                  }
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Event Name:</label>
                    <input
                      type="text"
                      value={newEvent.name}
                      onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Event Date:</label>
                    <input
                      type="date"
                      value={newEvent.event_date}
                      onChange={(e) => setNewEvent({...newEvent, event_date: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label>Start Time:</label>
                      <input
                        type="time"
                        value={newEvent.start_time}
                        onChange={(e) => setNewEvent({...newEvent, start_time: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                      />
                    </div>
                    <div>
                      <label>End Time:</label>
                      <input
                        type="time"
                        value={newEvent.end_time}
                        onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                      />
                    </div>
                    <div>
                      <label>Slot Duration (min):</label>
                      <input
                        type="number"
                        value={newEvent.slot_duration}
                        onChange={(e) => setNewEvent({...newEvent, slot_duration: parseInt(e.target.value)})}
                        required
                        min="5"
                        max="60"
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
                      {editingEvent.id ? 'Update' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setEditingEvent(null); setNewEvent({ name: '', event_date: '', start_time: '', end_time: '', slot_duration: 15 }); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
              {events.map((event) => (
                <div key={event.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', backgroundColor: '#f8f9fa' }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>{event.name}</h4>
                  <p style={{ margin: '5px 0', color: '#666' }}><strong>Date:</strong> {event.event_date}</p>
                  <p style={{ margin: '5px 0', color: '#666' }}><strong>Time:</strong> {event.start_time} - {event.end_time}</p>
                  <p style={{ margin: '5px 0', color: '#666' }}><strong>Slot Duration:</strong> {event.slot_duration} min</p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      onClick={() => {
                        setEditingEvent(event);
                        setNewEvent({
                          name: event.name,
                          event_date: event.event_date,
                          start_time: event.start_time,
                          end_time: event.end_time,
                          slot_duration: event.slot_duration
                        });
                      }}
                      style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this event?')) {
                          try {
                            await adminAPI.deleteEvent(event.id);
                            fetchEvents();
                          } catch (error) {
                            setError(error.response?.data?.message || 'Error deleting event');
                          }
                        }
                      }}
                      style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Buyer Management */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Buyer Management</h2>
          <button
            onClick={() => {
              setShowBuyers(!showBuyers);
              if (!showBuyers) fetchBuyers();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showBuyers ? 'Hide Buyers' : 'Show Buyers'}
          </button>
        </div>

        {showBuyers && (
          <div>
            <button
              onClick={() => setEditingBuyer({})}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '20px'
              }}
            >
              Add New Buyer
            </button>

            {editingBuyer !== null && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <h3>{editingBuyer.id ? 'Edit Buyer' : 'Add New Buyer'}</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingBuyer.id) {
                      await adminAPI.updateBuyer(editingBuyer.id, newBuyer);
                    } else {
                      const response = await adminAPI.createBuyer(newBuyer);
                      // Show password if buyer was created
                      if (response.data.password) {
                        setBuyerPasswords(prev => ({
                          ...prev,
                          [response.data.buyer.id]: response.data.password
                        }));
                        setShowBuyerPasswordModal(response.data.buyer.id);
                      }
                    }
                    setEditingBuyer(null);
                    setNewBuyer({ name: '', surname: '', email: '', phone: '', company_name: '', notes: '', event_id: '' });
                    fetchBuyers();
                  } catch (error) {
                    setError(error.response?.data?.message || 'Error saving buyer');
                  }
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label>Name:</label>
                      <input type="text" value={newBuyer.name} onChange={(e) => setNewBuyer({...newBuyer, name: e.target.value})} required style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                    </div>
                    <div>
                      <label>Surname:</label>
                      <input type="text" value={newBuyer.surname} onChange={(e) => setNewBuyer({...newBuyer, surname: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label>Email:</label>
                      <input type="email" value={newBuyer.email} onChange={(e) => setNewBuyer({...newBuyer, email: e.target.value})} required style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                    </div>
                    <div>
                      <label>Phone:</label>
                      <input type="text" value={newBuyer.phone} onChange={(e) => setNewBuyer({...newBuyer, phone: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Event (Fuar):</label>
                    <select
                      value={newBuyer.event_id}
                      onChange={(e) => setNewBuyer({...newBuyer, event_id: e.target.value})}
                      required={!editingBuyer.id}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginTop: '5px'
                      }}
                    >
                      <option value="">Select Event</option>
                      {events.map(event => (
                        <option key={event.id} value={event.id}>{event.name} - {event.event_date}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Company Name:</label>
                    <input type="text" value={newBuyer.company_name} onChange={(e) => setNewBuyer({...newBuyer, company_name: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Notes:</label>
                    <textarea value={newBuyer.notes} onChange={(e) => setNewBuyer({...newBuyer, notes: e.target.value})} rows="3" style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }} />
                  </div>
                  <div>
                    <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
                      {editingBuyer.id ? 'Update' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setEditingBuyer(null); setNewBuyer({ name: '', surname: '', email: '', phone: '', company_name: '', notes: '', event_id: '' }); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
              {buyers.map((buyer) => (
                <div key={buyer.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', backgroundColor: '#f8f9fa' }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>{buyer.name} {buyer.surname}</h4>
                  <p style={{ margin: '5px 0', color: '#666' }}><strong>Email:</strong> {buyer.email}</p>
                  {buyer.phone && <p style={{ margin: '5px 0', color: '#666' }}><strong>Phone:</strong> {buyer.phone}</p>}
                  {buyer.company_name && <p style={{ margin: '5px 0', color: '#666' }}><strong>Company:</strong> {buyer.company_name}</p>}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setEditingBuyer(buyer);
                        setNewBuyer({
                          name: buyer.name,
                          surname: buyer.surname || '',
                          email: buyer.email,
                          phone: buyer.phone || '',
                          company_name: buyer.company_name || '',
                          notes: buyer.notes || '',
                          event_id: buyer.event_id || ''
                        });
                      }}
                      style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowBuyerPasswordModal(buyer.id)}
                      style={{ padding: '5px 10px', backgroundColor: '#EDB404', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Password
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this buyer?')) {
                          try {
                            await adminAPI.deleteBuyer(buyer.id);
                            fetchBuyers();
                          } catch (error) {
                            setError(error.response?.data?.message || 'Error deleting buyer');
                          }
                        }
                      }}
                      style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User Management */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>User Management</h2>
          <button
            onClick={() => {
              setShowUsers(!showUsers);
              if (!showUsers) fetchUsers();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showUsers ? 'Hide Users' : 'Show Users'}
          </button>
        </div>

        {showUsers && (
          <div>
            {users.length === 0 ? (
              <p>No users found.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                {users.map((user) => (
                  <div key={user.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', backgroundColor: '#f8f9fa' }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>{user.name}</h4>
                    <p style={{ margin: '5px 0', color: '#666' }}><strong>Email:</strong> {user.email}</p>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          setConvertingUser(user.id);
                          setNewBuyerFromUser({
                            name: user.name,
                            surname: '',
                            company_name: '',
                            phone: '',
                            notes: '',
                            event_id: ''
                          });
                        }}
                        style={{ padding: '5px 10px', backgroundColor: '#EDB404', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Make Buyer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {convertingUser !== null && (
              <div style={{ marginTop: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
                <h3>Convert User to Buyer</h3>
                <form onSubmit={(e) => handleConvertUserToBuyer(e, convertingUser)}>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name *</label>
                    <input
                      type="text"
                      value={newBuyerFromUser.name}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, name: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Surname</label>
                    <input
                      type="text"
                      value={newBuyerFromUser.surname}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, surname: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Event *</label>
                    <select
                      value={newBuyerFromUser.event_id}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, event_id: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    >
                      <option value="">Select Event</option>
                      {events.map((event) => (
                        <option key={event.id} value={event.id}>{event.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Company Name</label>
                    <input
                      type="text"
                      value={newBuyerFromUser.company_name}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, company_name: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Phone</label>
                    <input
                      type="text"
                      value={newBuyerFromUser.phone}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, phone: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Notes</label>
                    <textarea
                      value={newBuyerFromUser.notes}
                      onChange={(e) => setNewBuyerFromUser({...newBuyerFromUser, notes: e.target.value})}
                      rows="3"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '5px' }}
                    />
                  </div>
                  <div>
                    <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
                      Convert to Buyer
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConvertingUser(null);
                        setNewBuyerFromUser({ name: '', surname: '', company_name: '', phone: '', notes: '', event_id: '' });
                      }}
                      style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Meetings List */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>All Meetings</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleExportMeetings}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Export to Excel
            </button>
            <button
              onClick={() => {
                setShowMeetings(!showMeetings);
                if (!showMeetings) fetchMeetings();
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showMeetings ? 'Hide Meetings' : 'Show Meetings'}
          </button>
          </div>
        </div>

        {showMeetings && (
          <div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Company</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Time</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Requester Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Requester Surname</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Requester Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Buyer</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((meeting) => (
                    <tr key={meeting.id}>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.id}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.company?.name || 'N/A'}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                        {(() => {
                          const slot = meeting.meetingSlot || meeting.meeting_slot;
                          if (!slot || !slot.meeting_date) return 'N/A';
                          return formatDate(slot.meeting_date);
                        })()}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                        {(() => {
                          const slot = meeting.meetingSlot || meeting.meeting_slot;
                          if (!slot || !slot.start_time || !slot.end_time) return 'N/A';
                          return `${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`;
                        })()}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.requester_name || meeting.user?.name || meeting.buyer?.name || 'N/A'}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.requester_surname || meeting.buyer?.surname || ''}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.requester_email || meeting.user?.email || meeting.buyer?.email || 'N/A'}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: meeting.status === 'approved' ? '#28a745' : meeting.status === 'pending' ? '#ffc107' : meeting.status === 'declined' ? '#dc3545' : '#6c757d',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {meeting.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{meeting.buyer ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Password Management</h3>
            
            {companyPasswords[showPasswordModal] ? (
              <div>
                <p style={{ margin: '0 0 15px 0', color: '#666' }}>Current Password:</p>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '4px',
                  border: '2px solid #EDB404',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  {companyPasswords[showPasswordModal]}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleResetPassword(showPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleGeneratePassword(showPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Generate New
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 20px 0', color: '#666' }}>No password set for this company.</p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleResetPassword(showPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Set Manual Password
                  </button>
                  <button
                    onClick={() => handleGeneratePassword(showPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Generate Random
                  </button>
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={closePasswordModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buyer Password Modal */}
      {showBuyerPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Buyer Password Management</h3>
            
            {buyerPasswords[showBuyerPasswordModal] ? (
              <div>
                <p style={{ margin: '0 0 15px 0', color: '#666' }}>Current Password:</p>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '4px',
                  border: '2px solid #EDB404',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  {buyerPasswords[showBuyerPasswordModal]}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleResetBuyerPassword(showBuyerPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleGenerateBuyerPassword(showBuyerPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Generate New
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 20px 0', color: '#666' }}>No password set for this buyer.</p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleResetBuyerPassword(showBuyerPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Set Manual Password
                  </button>
                  <button
                    onClick={() => handleGenerateBuyerPassword(showBuyerPasswordModal)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Generate Random
                  </button>
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowBuyerPasswordModal(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
